import { Show, useModal } from "@refinedev/antd";
import { Tag, Space, Button } from "antd";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import {
  EnvironmentOutlined,
  BankOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import { IAddress } from "interfaces";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
} from "components/show";

export const AddressShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IAddress>();
  const record = result;

  const {
    modalProps: modalPropsShipco,
    show: showShipco,
    close: closeShipco,
  } = useModal();

  const {
    modalProps: modalPropsShipcoGroup,
    show: showShipcoGroup,
    close: closeShipcoGroup,
  } = useModal();

  const hasShipco = !!record?.shipco;
  const hasShipcoGroup = !!record?.shipcoGroup;

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "active address",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Space>
          <Button
            type="default"
            onClick={showShipco}
            disabled={!hasShipco}
          >
            Shipco Details
          </Button>
          <Button
            type="default"
            onClick={showShipcoGroup}
            disabled={!hasShipcoGroup}
          >
            Shipco Group Details
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Address Details */}
        <ShowSection
          title="Address Details"
          subtitle="Physical location information"
          icon={<EnvironmentOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Address Line 1" value={record?.addressLine1} />
            <ShowField label="Address Line 2" value={record?.addressLine2} />
            <ShowField label="Address Line 3" value={record?.addressLine3} />
            <ShowField label="City" value={record?.city} />
            <ShowField label="State" value={record?.state} />
            <ShowField label="Postal Code" value={record?.postalCode} type="code" />
            <ShowField label="Country" value={record?.country?.name} />
          </ShowFieldGrid>
        </ShowSection>

        {/* Associated Entity */}
        <ShowSection
          title="Associated Entity"
          subtitle="Related business entity"
          icon={<BankOutlined />}
          variant="default"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Shipco" value={record?.shipco?.registrationName} />
            <ShowField label="Shipco Group" value={record?.shipcoGroup?.registrationName} />
          </ShowFieldGrid>
        </ShowSection>
      </Space>

      {/* Shipco Modal */}
      <ShowModal
        title="Shipco Details"
        subtitle={record?.shipco?.registrationName}
        icon={<BankOutlined />}
        open={modalPropsShipco.open ?? false}
        onClose={closeShipco}
        empty={!hasShipco}
        emptyText="No shipco associated with this address"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Registration Name" value={record?.shipco?.registrationName} />
          <ShowField label="Registration Number" value={record?.shipco?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.shipco?.gstNumber} type="code" copyable />
          <ShowField label="Accreditation Number" value={record?.shipco?.accreditationNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.shipco?.phone} type="phone" />
          <ShowField label="Communication Email" value={record?.shipco?.email} type="email" />
        </ShowFieldGrid>
      </ShowModal>

      {/* Shipco Group Modal */}
      <ShowModal
        title="Shipco Group Details"
        subtitle={record?.shipcoGroup?.registrationName}
        icon={<TeamOutlined />}
        open={modalPropsShipcoGroup.open ?? false}
        onClose={closeShipcoGroup}
        empty={!hasShipcoGroup}
        emptyText="No shipco group associated with this address"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Registration Name" value={record?.shipcoGroup?.registrationName} />
          <ShowField label="Registration Number" value={record?.shipcoGroup?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.shipcoGroup?.gstNumber} type="code" copyable />
          <ShowField label="Accreditation Number" value={record?.shipcoGroup?.accreditationNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.shipcoGroup?.phone} type="phone" />
          <ShowField label="Communication Email" value={record?.shipcoGroup?.email} type="email" />
        </ShowFieldGrid>
      </ShowModal>
    </Show>
  );
};
